import { isPlatformBrowser, NgIf } from '@angular/common';
import {
	AfterViewInit,
	Component,
	Inject,
	OnInit,
	PLATFORM_ID,
	ViewChild,
} from '@angular/core';
import { ActivatedRoute, Router, RouterOutlet } from '@angular/router';
import { ScriptLoaderService } from './global/services/script-loader.service';
import { AccountService } from './pages/account/services/account.service';
import { environment } from 'src/environments/environment';
import { CustomDomainService } from './global/services/custom-domain.service';
import { MatDialog } from '@angular/material/dialog';
import { NotificationService } from './shared/services/notification.service';
import { MatDrawer } from '@angular/material/sidenav';
import { FooterComponent } from './global/components/footer/footer.component';
import { SidenavComponent } from './global/components/sidenav/sidenav.component';
import { MatSidenavModule } from '@angular/material/sidenav';
import { HeaderComponent } from './global/components/header/header.component';

@Component({
	selector: 'app-root',
	templateUrl: './app.component.html',
	styleUrls: ['./app.component.scss'],
	standalone: true,
	imports: [
		HeaderComponent,
		MatSidenavModule,
		NgIf,
		SidenavComponent,
		RouterOutlet,
		FooterComponent,
	],
})
/** *App Component */
export class AppComponent implements OnInit, AfterViewInit {
	title: string = 'WhyDonate: Personal Crowdfunding and Charity Fundraising';
	isBrowser: boolean = false;
	router: Router;
	isLoggedIn: Boolean = false;
	currentDomain: string = '';
	officialDomains = environment.OFFICIAL_DOMAINS;
	@ViewChild('drawer') drawer!: MatDrawer;

	constructor(
		@Inject(PLATFORM_ID) platformId: Object,
		private _scriptLoader: ScriptLoaderService,
		router: Router,
		public activtedRoute: ActivatedRoute,
		public customDomainService: CustomDomainService,
		public accountService: AccountService,
		public notificationService: NotificationService
	) {
		this.isBrowser = isPlatformBrowser(platformId);
		this.router = router;
	}

	ngAfterViewInit(): void {
		this.checkAndSetCustomDomain();

		if (this.isBrowser) {
			this._scriptLoader.loadCrisp();
		}
		this.updateSidenavClass();

		// Listen for sidenav toggle changes
		if (this.drawer) {
			this.drawer.openedChange.subscribe(() => {
				this.updateSidenavClass();
			});
		}
		/*
		 * Disable Console on prod
		 */
		if (environment.production) {
			console.log = console.debug = console.error = () => {};
		}
	}

	updateSidenavClass() {
		document.body.classList.remove(
			'sidenav-expanded',
			'sidenav-collapsed',
			'sidenav-hidden'
		);

		// ✅ If user is logged out, add "sidenav-hidden"
		if (!this.isLoggedIn || !this.drawer) {
			document.body.classList.add('sidenav-hidden');
			return;
		}

		// ✅ Add class based on sidenav state
		if (this.drawer.opened) {
			document.body.classList.add('sidenav-expanded');
		} else {
			document.body.classList.add('sidenav-collapsed');
		}
	}

	/**
	 * Function to check if the current domain matches any official domain
	 * and sets the custom domain flag accordingly.
	 *
	 * @param officialDomains - Array of official domain strings.
	 * @param currentDomain - The current domain URL.
	 * @param customDomainService - The service to set the custom domain flag.
	 * @returns void
	 */
	checkAndSetCustomDomain(): void {
		try {
			let officialDomains = this.officialDomains;
			let currentDomain = window?.location?.href;
			let isCustomDomainFound: boolean = true; // Default to true assuming it's a custom domain

			// Validate that officialDomains is an array and currentDomain is a non-empty string
			if (!Array.isArray(officialDomains) || officialDomains.length === 0) {
				throw new Error("The 'officialDomains' array is invalid or empty.");
			}

			if (typeof currentDomain !== 'string' || currentDomain.trim() === '') {
				throw new Error(
					"The 'currentDomain' must be a valid non-empty string."
				);
			}

			// Loop through official domains and check if the current domain matches
			officialDomains.forEach((domain: string) => {
				if (typeof domain !== 'string' || domain.trim() === '') {
					console.warn(`Skipping invalid domain: ${domain}`);
					return; // Skip invalid domain entries
				}

				if (currentDomain.includes(domain)) {
					isCustomDomainFound = false; // It's an official domain, not a custom one
				}
			});

			// Set the custom domain flag using the provided service
			this.customDomainService.setIsCustomDomainValue(isCustomDomainFound);
		} catch (error) {
			console.error(
				'An error occurred while checking the custom domain:',
				error
			);
			// You can also notify the user or report the error to a monitoring service here
		}
	}

	ngOnInit(): void {
		this.accountService.isLoggedIn.subscribe((res) => {
			this.isLoggedIn = res;

			setInterval(() => {
				if (
					this.accountService.isLoggedIn.getValue() &&
					this.accountService.isJWTExpired()
				) {
					this.accountService.autoLogout();
				}
			}, 2000);

			// ✅ HOTFIX FOR CHROME CRASH
			const observer = new MutationObserver(() => {
				const elements = document.querySelectorAll('[aria-owns]');
				elements.forEach((element) => {
					element.removeAttribute('aria-owns');
				});
			});

			observer.observe(document.body, {
				childList: true,
				subtree: true,
			});
		});
	}
}
